/**
 * useTutorShellState — les gestes de la coquille du tuteur : la feuille de profil ouverte ou non, les
 * portes qu'elle ouvre, « Suivre mes paiements » et « Se déconnecter ».
 *
 * « Suivre mes paiements » : `useStripeDashboard` ; la ligne tourne pendant la demande, la feuille
 * reste ouverte. Un refus se dit dans le `<Snackbar>` de la coquille (`paymentsError`), avec le message du
 * serveur : la boîte réseau de l'ancien kit se tait sous l'espace tuteur.
 *
 * Les portes dont l'écran n'est pas encore câblé poussent leur route : le pont de la route
 * (`tutorSpaceBridge`) rend l'ancien écran tant que son lot n'a rien inscrit — pas de cul-de-sac.
 *
 * La feuille s'ouvre aussi sur demande (`useTutorProfileSheetRequest`) : la route `account`, qui n'a
 * plus de page, y ramène.
 */
import { useNavigation } from "@react-navigation/native"
import { useCallback, useState } from "react"

import { useGestureNotice } from "../../hooks/useGestureNotice"
import { useLogoutAdapter } from "../../hooks/useLogoutAdapter"
import { useStripeDashboard } from "../../hooks/useStripeDashboard"
import { useTutorProfileSheetRequest } from "./Account/tutorProfileSheetRequest"

/** Ce que dit « Suivre mes paiements » refusé sans message du serveur. */
export const TUTOR_PAYMENTS_FAILED = "Tes paiements n'ont pas pu s'ouvrir. Vérifie ta connexion puis réessaie."

type PushNavigation = { push: (route: string, params?: object) => void; navigate: (route: string) => void }

export function useTutorShellState() {
    const navigation = useNavigation() as unknown as PushNavigation
    const [sheetOpen, setSheetOpen] = useState(false)
    const { logout, loggingOut } = useLogoutAdapter()
    useTutorProfileSheetRequest(() => setSheetOpen(true))

    const paymentsNotice = useGestureNotice(TUTOR_PAYMENTS_FAILED)
    const payments = useStripeDashboard(paymentsNotice.tell)

    const push = useCallback((route: string, params?: object) => navigation.push(route, params), [navigation])

    return {
        sheetOpen,
        openSheet: () => setSheetOpen(true),
        closeSheet: () => setSheetOpen(false),
        push,
        openHome: () => navigation.navigate("dashboard"),
        openPayments: () => {
            paymentsNotice.dismiss()
            payments.open()
        },
        paymentsLoading: payments.opening,
        paymentsError: paymentsNotice.notice,
        dismissPaymentsError: paymentsNotice.dismiss,
        logout,
        loggingOut,
    }
}
